import { Input, useErrorHandler } from '@sandtech-hub-monorepo/ui-kit';
import { FileTextIcon, UploadCloudIcon } from 'lucide-react';
import React, { createRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

interface Props {
  value?: File;
  onChange: (value: File | undefined) => void;
}

export const PDFFileUpload = ({ value, onChange }: Props) => {
  const cvUploadRef = createRef<HTMLInputElement>();
  const { handleGeneralAppError } = useErrorHandler();

  const handleFileChange = useCallback(
    (files: File[]) => {
      if (files.length) {
        const file = files[0];

        if (file && file.type.substring(0, 15) === 'application/pdf') {
          // Max file size 10MB.
          if (file.size > 10 * 1000 * 1000) {
            handleGeneralAppError(
              'The file you selected is too large! Please try again with a PDF no larger than 10 MB.'
            );
            return;
          }

          const reader = new FileReader();
          reader.onloadend = async (e) => {
            onChange(file);
          };
          reader.readAsDataURL(file);
        }
      } else {
        handleGeneralAppError(
          'The file you selected is not valid! Please try again with a PDF no larger than 10 MB.'
        );
      }
    },
    [handleGeneralAppError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileChange,
    multiple: false,
    accept: {
      'application/pdf': [],
    },
    onError(err) {
      console.log(err.message);
    },
  });

  return (
    <div
      className="p-2 flex-1 flex flex-col items-center justify-around rounded-md border hover:bg-muted cursor-pointer"
      {...getRootProps()}
    >
      <Input
        {...getInputProps()}
        onChange={() =>
          handleFileChange((cvUploadRef.current?.files || []) as File[])
        }
        className="hidden"
        ref={cvUploadRef}
        type="file"
      />
      <div
        className="h-full w-full flex flex-col p-6 items-center justify-center rounded-md bg-background-tertiary"
        onClick={() => cvUploadRef.current?.click()}
      >
        {isDragActive ? (
          <div className="flex flex-col gap-2 items-center">
            <div className="h-10 p-2 aspect-square rounded-lg border border-border">
              <UploadCloudIcon className="stroke-muted-foreground" />
            </div>
            <div className="text-center">
              <span className="font-medium text-primary">
                Drop your file here
              </span>
            </div>
          </div>
        ) : value ? (
          <div className="flex flex-col gap-2 items-center">
            <div className="h-10 p-2 aspect-square rounded-lg border border-border">
              <FileTextIcon className="stroke-muted-foreground" />
            </div>
            <span className="font-medium  text-muted-foreground">
              {value.name}
            </span>
            <span className="text-xs text-center text-primary">
              Click to choose file{' '}
              <span className="text-foreground font-light">
                or drag and drop
              </span>
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-2 items-center">
            <div className="h-10 p-2 aspect-square rounded-lg border border-border">
              <UploadCloudIcon className="stroke-muted-foreground" />
            </div>
            <span className="font-medium text-primary text-sm">
              Click to upload{' '}
              <span className="text-foreground font-light">
                or drag and drop
              </span>
            </span>
            <span className="text-sm text-center text-muted-foreground">
              PDF (max. 10MB)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
